import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, FileText } from "@phosphor-icons/react";

const NO_CUSTOMER = "No customer";

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "-";
  }
};

export function QuotesListPanel({ quotes, activeId, canDelete, onSelect, onNew, onRename, onDeleteRequest, showCustomerFilter = true }) {
  const [customerFilter, setCustomerFilter] = useState("all");
  const customerOptions = useMemo(
    () => [...new Set(quotes.map((q) => q.customer?.name || NO_CUSTOMER))].sort(),
    [quotes]
  );
  const filteredQuotes =
    !showCustomerFilter || customerFilter === "all"
      ? quotes
      : quotes.filter((q) => (q.customer?.name || NO_CUSTOMER) === customerFilter);

  return (
    <aside className="flex w-full shrink-0 flex-col border-b border-zinc-300 sm:w-80 sm:border-b-0 sm:border-r" data-testid="quotes-list-panel">
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-[0.05em] text-zinc-500">Quotes</span>
        <Button
          size="sm"
          className="h-7 rounded-sm bg-blue-600 px-2 text-white hover:bg-blue-700"
          onClick={onNew}
          data-testid="new-quote-button"
        >
          <Plus size={13} className="mr-1" /> New Quote
        </Button>
      </div>
      {showCustomerFilter && (
        <div className="border-b border-zinc-200 px-3 py-2">
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger className="h-8 w-full rounded-sm border-zinc-300 text-sm" data-testid="quotes-customer-filter-select">
              <SelectValue placeholder="All customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {customerOptions.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        {filteredQuotes.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center gap-1 text-zinc-400">
            <FileText size={22} />
            <span className="text-xs">{quotes.length === 0 ? "No quotes yet" : "No quotes for this customer"}</span>
          </div>
        ) : (
          filteredQuotes.map((q) => (
            <div
              key={q.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(q)}
              onKeyDown={(e) => e.key === "Enter" && onSelect(q)}
              className={`flex cursor-pointer items-center gap-2 border-b border-zinc-100 px-3 py-2.5 transition-colors duration-150 ${
                q.id === activeId ? "bg-blue-50" : "hover:bg-zinc-50"
              }`}
              data-testid={`quote-row-${q.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`truncate text-sm font-semibold ${q.id === activeId ? "text-blue-700" : "text-zinc-900"}`}>
                    {q.customer_name}
                  </span>
                  <span className="shrink-0 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">
                    {q.item_count}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-zinc-500">
                  <span className="truncate">
                    {q.customer ? `${q.customer.name} (${q.customer.currency})` : NO_CUSTOMER}
                  </span>
                  <span className="shrink-0 text-zinc-400">{fmtDate(q.created_at)}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  className="rounded-sm p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-blue-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(q);
                  }}
                  data-testid={`rename-quote-button-${q.id}`}
                >
                  <PencilSimple size={14} />
                </button>
                <button
                  type="button"
                  className="rounded-sm p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                  disabled={!canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteRequest(q);
                  }}
                  data-testid={`delete-quote-button-${q.id}`}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
